import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Typography,
  Radio,
  CircularProgress,
  Box,
  useTheme,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import EmailIcon from "@mui/icons-material/Email";
import { User, UserSize } from "@/app/graphql/codegen";
import { useLazyQuery, useMutation } from "@apollo/client";
import { SWAP_USER_SEATS } from "../graphql/mutations/swapUserSeats";
import { MOVE_USER_TO_SEAT } from "@settings/graphql/mutations/moveUserToSeat";
import { UPDATE_INVITE_LINK_SIZE } from "@company/graphql/mutations/updateInviteLinkSize";
import { SEAT_SWAP_PREVIEW } from "../graphql/queries/seatSwapPreview";
import { FETCH_COMPANY_SEATS } from "@company/graphql/queries/fetchCompanySeats";

// Type for invite links (simplified)
interface InviteLink {
  id: string;
  email: string;
  size: UserSize;
  role: string;
  isUsed: boolean;
}

interface SeatSwapDialogProps {
  open: boolean;
  onClose: () => void;
  sourceUser?: User;
  sourceInviteLink?: InviteLink;
  users: User[];
  inviteLinks?: InviteLink[];
  emptySeatSizes: UserSize[]; // e.g., ["S", "L"]
}

export const SeatSwapDialog: React.FC<SeatSwapDialogProps> = ({ 
  open, 
  onClose, 
  sourceUser, 
  sourceInviteLink, 
  users, 
  inviteLinks = [], 
  emptySeatSizes 
}) => {
  const theme = useTheme();
  const [targetId, setTargetId] = useState<string | null>(null); // user id, invite id, or synthetic seat key
  const [previewErrors, setPreviewErrors] = useState<string[] | null>(null);

  // Validate that exactly one source is provided
  if (!sourceUser && !sourceInviteLink) {
    throw new Error("Either sourceUser or sourceInviteLink must be provided");
  }
  if (sourceUser && sourceInviteLink) {
    throw new Error("Only one of sourceUser or sourceInviteLink should be provided");
  }

  const isInviteLinkSource = !!sourceInviteLink;

  const [runPreview, { loading: previewLoading }] = useLazyQuery(SEAT_SWAP_PREVIEW, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (data?.seatSwapPreview?.success) {
        setPreviewErrors(null);
      } else {
        setPreviewErrors(data?.seatSwapPreview?.errors || ["Unbekannter Fehler"]);
      }
    },
    onError: (err) => setPreviewErrors([err.message]),
  });

  const [swapUsers, { loading: swapping }] = useMutation(SWAP_USER_SEATS, {
    refetchQueries: [{ query: FETCH_COMPANY_SEATS }],
    awaitRefetchQueries: true,
    onCompleted: onClose,
  });

  const [moveUserToSeat, { loading: moving }] = useMutation(MOVE_USER_TO_SEAT, {
    refetchQueries: [{ query: FETCH_COMPANY_SEATS }],
    awaitRefetchQueries: true,
    onCompleted: onClose,
  });

  const [updateInviteLinkSize, { loading: updatingInviteLink }] = useMutation(UPDATE_INVITE_LINK_SIZE, {
    refetchQueries: [{ query: FETCH_COMPANY_SEATS }],
    awaitRefetchQueries: true,
    onCompleted: onClose,
  });

  // Prepare separate arrays for better visual grouping
  const userTargets = useMemo(() => {
    if (isInviteLinkSource) {
      return users; // Invite links can swap with any user
    }
    return users.filter((u) => u.id !== sourceUser!.id); // Filter out source user
  }, [users, sourceUser, isInviteLinkSource]);
  
  const inviteLinkTargets = useMemo(() => {
    if (isInviteLinkSource) {
      return inviteLinks.filter((link) => link.id !== sourceInviteLink!.id && !link.isUsed); // Filter out source invite link and used links
    }
    return inviteLinks.filter((link) => !link.isUsed); // Users can swap with any unused invite link
  }, [inviteLinks, sourceInviteLink, isInviteLinkSource]);
  
  const seatTargets = useMemo(() => emptySeatSizes.map((s) => ({ id: `SEAT_${s}`, size: s })), [emptySeatSizes]);

  useEffect(() => {
    if (!targetId) return;
    
    // Only run preview for user-to-user swaps
    if (!isInviteLinkSource && !targetId.startsWith("SEAT_") && !targetId.startsWith("INVITE_")) {
      runPreview({ variables: { user1Id: sourceUser!.id, user2Id: targetId } });
    } else {
      // For invite link operations or user-to-empty-seat: validate locally
      setPreviewErrors(null);
    }
  }, [targetId, isInviteLinkSource, sourceUser]);

  const handleConfirm = () => {
    if (!targetId) return;
    
    if (isInviteLinkSource) {
      // Invite link is the source
      if (targetId.startsWith("SEAT_")) {
        // Invite link → Empty seat: just update invite link size
        const seatSize = targetId.replace("SEAT_", "") as UserSize;
        updateInviteLinkSize({ variables: { id: sourceInviteLink!.id, size: seatSize } });
      } else if (targetId.startsWith("INVITE_")) {
        // Invite link → Invite link: swap sizes
        const targetInviteId = targetId.replace("INVITE_", "");
        const targetInvite = inviteLinkTargets.find(link => link.id === targetInviteId);
        if (targetInvite) {
          // Update both invite links (do them sequentially to avoid conflicts)
          updateInviteLinkSize({ 
            variables: { id: sourceInviteLink!.id, size: targetInvite.size },
            onCompleted: () => {
              updateInviteLinkSize({ variables: { id: targetInviteId, size: sourceInviteLink!.size } });
            }
          });
        }
      } else {
        // Invite link → User: move user to invite link's seat, update invite link to user's seat
        const targetUser = userTargets.find(user => user.id === targetId);
        if (targetUser) {
          moveUserToSeat({ 
            variables: { userId: targetId, seatType: sourceInviteLink!.size },
            onCompleted: () => {
              updateInviteLinkSize({ variables: { id: sourceInviteLink!.id, size: targetUser.size } });
            }
          });
        }
      }
    } else {
      // User is the source  
      if (targetId.startsWith("SEAT_")) {
        // User → Empty seat
        const seatSize = targetId.replace("SEAT_", "") as UserSize;
        moveUserToSeat({ variables: { userId: sourceUser!.id, seatType: seatSize } });
      } else if (targetId.startsWith("INVITE_")) {
        // User → Invite link: move user to invite link's seat, update invite link to user's seat
        const targetInviteId = targetId.replace("INVITE_", "");
        const targetInvite = inviteLinkTargets.find(link => link.id === targetInviteId);
        if (targetInvite) {
          moveUserToSeat({ 
            variables: { userId: sourceUser!.id, seatType: targetInvite.size },
            onCompleted: () => {
              updateInviteLinkSize({ variables: { id: targetInviteId, size: sourceUser!.size } });
            }
          });
        }
      } else {
        // User → User
        swapUsers({ variables: { user1Id: sourceUser!.id, user2Id: targetId } });
      }
    }
  };

  const disabled = !targetId || !!previewErrors || previewLoading || swapping || moving || updatingInviteLink;

  const renderPreviewInfo = () => {
    if (!targetId) return null;
    if (previewLoading) return null;
    if (previewErrors) {
      return (
        <Box mt={2} color={theme.palette.error.main}>
          {previewErrors.map((e) => (
            <Typography key={e} variant="body2">
              {e}
            </Typography>
          ))}
        </Box>
      );
    }

    // Success path – compute descriptive message
    if (targetId.startsWith("SEAT_")) {
      const seatSize = targetId.replace("SEAT_", "");
      const sourceName = isInviteLinkSource ? sourceInviteLink!.email : sourceUser!.name;
      return (
        <Box mt={2} color={theme.palette.success.main}>
          <Typography variant="body2">
            Nach dem Tausch erhält <strong>{sourceName}</strong> einen freien {seatSize}-Seat.
          </Typography>
        </Box>
      );
    } else if (targetId.startsWith("INVITE_")) {
      const targetInviteId = targetId.replace("INVITE_", "");
      const targetInvite = inviteLinkTargets.find(link => link.id === targetInviteId);
      if (!targetInvite) return null;
      const sourceName = isInviteLinkSource ? sourceInviteLink!.email : sourceUser!.name;
      return (
        <Box mt={2} color={theme.palette.success.main}>
          <Typography variant="body2">
            Nach dem Tausch erhält <strong>{sourceName}</strong> den {targetInvite.size}-Seat von <strong>{targetInvite.email}</strong> und umgekehrt.
          </Typography>
        </Box>
      );
    } else {
      const target = users.find((u) => u.id === targetId);
      if (!target) return null;
      const sourceName = isInviteLinkSource ? sourceInviteLink!.email : sourceUser!.name;
      return (
        <Box mt={2} color={theme.palette.success.main}>
          <Typography variant="body2">
            Nach dem Tausch erhält <strong>{sourceName}</strong> den {target.size}-Seat von <strong>{target.name}</strong> und umgekehrt.
          </Typography>
        </Box>
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Seat tauschen</DialogTitle>
      <DialogContent dividers>
        <Typography gutterBottom>
          Wähle ein Ziel für den Tausch von{" "}
          <strong>
            {isInviteLinkSource ? sourceInviteLink!.email : sourceUser!.name}
          </strong>{" "}
          ({isInviteLinkSource ? sourceInviteLink!.size : sourceUser!.size})
        </Typography>
        <List
          subheader={<li />}
          sx={{
            width: "100%",
            bgcolor: "background.paper",
            maxHeight: 300,
            overflowY: "auto",
          }}
        >
          {/* Users section */}
          {userTargets.length > 0 && (
            <li>
              <ul style={{ padding: 0 }}>
                <ListSubheader>User</ListSubheader>
                {userTargets.map((u) => {
                  const id = u.id;
                  return (
                    <ListItemButton key={id} selected={targetId === id} onClick={() => setTargetId(id)}>
                      <Radio checked={targetId === id} />
                      <PersonIcon sx={{ mr: 1 }} />
                      <ListItemText primary={`${u.name} (${u.size})`} />
                    </ListItemButton>
                  );
                })}
              </ul>
            </li>
          )}

          {/* Invite links section */}
          {inviteLinkTargets.length > 0 && (
            <li>
              <ul style={{ padding: 0 }}>
                <ListSubheader>Einladungslinks</ListSubheader>
                {inviteLinkTargets.map((invite) => {
                  const id = `INVITE_${invite.id}`;
                  return (
                    <ListItemButton key={id} selected={targetId === id} onClick={() => setTargetId(id)}>
                      <Radio checked={targetId === id} />
                      <EmailIcon sx={{ mr: 1 }} />
                      <ListItemText primary={`${invite.email} (${invite.size})`} />
                    </ListItemButton>
                  );
                })}
              </ul>
            </li>
          )}

          {/* Free seats section */}
          {seatTargets.length > 0 && (
            <li>
              <ul style={{ padding: 0 }}>
                <ListSubheader>Freie Plätze</ListSubheader>
                {seatTargets.map((seat) => {
                  const id = seat.id;
                  return (
                    <ListItemButton key={id} selected={targetId === id} onClick={() => setTargetId(id)}>
                      <Radio checked={targetId === id} />
                      <EventSeatIcon sx={{ mr: 1 }} />
                      <ListItemText primary={`Freier ${seat.size}-Seat`} />
                    </ListItemButton>
                  );
                })}
              </ul>
            </li>
          )}
        </List>

        {renderPreviewInfo()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant="contained" disabled={disabled} onClick={handleConfirm}>
          {(swapping || moving || updatingInviteLink) ? <CircularProgress size={20} color="inherit" /> : "Tauschen"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 